import React, { memo, useCallback, useEffect, useState, useRef } from "react";

/* ------------------------------------------------------------------ */
/* ✨ PROP TYPES                                                       */
/* ------------------------------------------------------------------ */
export const AnimationVariant = {
  SLIDE: "slide",
  FADE: "fade",
  VERTICAL: "vertical",
};

/* ------------------------------------------------------------------ */
/* ✨ UTILITIES                                                        */
/* ------------------------------------------------------------------ */
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const pickVariant = () =>
  [AnimationVariant.SLIDE, AnimationVariant.FADE, AnimationVariant.VERTICAL][
    randInt(0, 2)
  ];

/* ------------------------------------------------------------------ */
/* ✨ COMPONENT                                                        */
/* ------------------------------------------------------------------ */
/**
 * BeforeAfterPreview displays a before/after image comparison with animation
 *
 * @param {string} before - URL of the before image
 * @param {string} after - URL of the after image
 * @param {string} animationVariant - Variant of the animation (SLIDE, FADE, VERTICAL)
 * @param {number} cycleDuration - Duration of the animation cycle
 * @param {number} lineThickness - Thickness of the divider line
 * @param {boolean} randomize - Whether to randomize the animation variant
 * @param {Array} durationRange - Range of the animation duration
 * @param {Object} style - Additional styles for the container
 * @param {string} className - Additional classes for the container
 */
const BeforeAfterPreview = memo(
  ({
    before,
    after,
    animationVariant,
    cycleDuration,
    lineThickness = 3,
    randomize = false,
    durationRange = [2500, 4500],
    style = {},
    className = "",
  }) => {
    /* ---------- 1. STABLE RANDOMISATION (runs once per mount) ---------- */
    const [variant] = useState(() => {
      if (randomize && !animationVariant) return pickVariant();
      return animationVariant ?? AnimationVariant.SLIDE;
    });

    const [duration] = useState(() => {
      if (randomize && !cycleDuration) {
        const [min, max] = durationRange;
        return randInt(min, max);
      }
      return cycleDuration ?? 3000;
    });

    /* ---------- 2. VISIBILITY TRACKING (pause when off-screen) --------- */
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const element = containerRef.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
        },
        { threshold: 0.1 }
      );

      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    /* ---------- 3. MEASURE PARENT SIZE (for masks) --------------------- */
    const [size, setSize] = useState({ w: 0, h: 0 });
    const [progress, setProgress] = useState(0);

    const onResize = useCallback(() => {
      const element = containerRef.current;
      if (element) {
        const rect = element.getBoundingClientRect();
        setSize({ w: rect.width, h: rect.height });
      }
    }, []);

    /* ---------- 4. ANIMATION LOOP (only runs when visible) ------------ */
    useEffect(() => {
      // Don't run animation if not visible
      if (!isVisible) return;

      let animationId;
      let startTime;

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const cycle = elapsed % duration;
        const halfDuration = duration / 2;

        // Create back-and-forth motion
        let normalizedProgress;
        if (cycle <= halfDuration) {
          normalizedProgress = cycle / halfDuration;
        } else {
          normalizedProgress = 1 - (cycle - halfDuration) / halfDuration;
        }

        setProgress(normalizedProgress);
        animationId = requestAnimationFrame(animate);
      };

      const ready =
        variant === AnimationVariant.FADE ||
        (variant === AnimationVariant.SLIDE && size.w) ||
        (variant === AnimationVariant.VERTICAL && size.h);

      if (ready) {
        animationId = requestAnimationFrame(animate);
      }

      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }, [variant, size, duration, isVisible]);

    /* ---------- 5. RESIZE OBSERVER ----------------------------------- */
    useEffect(() => {
      const element = containerRef.current;
      if (!element) return;

      const resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(element);
      onResize(); // Initial measurement

      return () => {
        resizeObserver.disconnect();
      };
    }, [onResize]);

    /* ---------- 5. COMPUTED STYLES ----------------------------------- */
    const containerStyle = {
      position: "relative",
      overflow: "hidden",
      ...style,
    };

    const baseImageStyle = {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    };

    const slideMaskStyle = {
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      width: `${size.w * progress}px`,
      overflow: "hidden",
      pointerEvents: "none",
    };

    const verticalMaskStyle = {
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "100%",
      height: `${size.h * progress}px`,
      overflow: "hidden",
      pointerEvents: "none",
    };

    const fadeOverlayStyle = {
      ...baseImageStyle,
      opacity: progress,
    };

    const dividerStyle = {
      position: "absolute",
      background:
        "linear-gradient(to bottom, #4d4d4d, #d9d9d9, #fafafa, #d9d9d9, #4d4d4d)",
      ...(variant === AnimationVariant.SLIDE
        ? {
            right: -lineThickness / 2,
            width: lineThickness,
            height: "100%",
          }
        : {
            top: 0,
            width: "100%",
            height: lineThickness,
            background:
              "linear-gradient(to right, #4d4d4d, #d9d9d9, #fafafa, #d9d9d9, #4d4d4d)",
          }),
    };

    /* ---------- 7. RENDER --------------------------------------------- */
    return (
      <div
        ref={containerRef}
        style={containerStyle}
        className={className}
      >
        {/* BASE – BEFORE IMAGE */}
        <img src={before} alt="Before" style={baseImageStyle} />

        {/* ░░░ FADE VARIANT ░░░ */}
        {variant === AnimationVariant.FADE && (
          <img src={after} alt="After" style={fadeOverlayStyle} />
        )}

        {/* ░░░ SLIDE VARIANT ░░░ */}
        {variant === AnimationVariant.SLIDE && size.w > 0 && (
          <div style={slideMaskStyle}>
            <img
              src={after}
              alt="After"
              style={{
                width: size.w,
                height: size.h,
                objectFit: "cover",
              }}
            />
            {/* shiny vertical divider */}
            <div style={dividerStyle} />
          </div>
        )}

        {/* ░░░ VERTICAL VARIANT ░░░ */}
        {variant === AnimationVariant.VERTICAL && size.h > 0 && (
          <div style={verticalMaskStyle}>
            <img
              src={after}
              alt="After"
              style={{
                width: size.w,
                height: size.h,
                objectFit: "cover",
                position: "absolute",
                bottom: 0,
              }}
            />
            {/* shiny horizontal divider */}
            <div style={dividerStyle} />
          </div>
        )}
      </div>
    );
  }
);

BeforeAfterPreview.displayName = "BeforeAfterPreview";

export default BeforeAfterPreview;
